import { CustomDatePickerInput } from "@components/datePicker/CustomDatePickerInput"
import { isFeatureAvailable, useGetDevFeatures } from "@components/featureGuard/FeatureGuard"
import { ModalNL } from "@components/modal/Modal"
import { CustomCalendarContainer } from "@components/plot/utils"
import { PlaceHolderShipImgContainer } from "@components/shipImgContainer/ShipImgContainer"
import { CellSetterV2 } from "@components/table/BodyContentLayoutNL"
import { LABELS_MONTHS } from "@helpers/getLabelsMonths"
import { formatString } from "@helpers/makeLabels"
import { useGetOpModesListQuery } from "@queries/useGetOpModesListQuery"
import { useAppSelector } from "@redux/app/hooks"
import { getCurrentCompany } from "@redux/features/currentCompany/currentCompanySlice"
import { getUserData, PortalMode } from "@redux/features/userData/userDataSlice"
import AHTSWaningPanel from "@sop/FuelPage/Total/AHTSWaningPanel"
import TableBodyOperationsTimelineNL from "@sop/OperationsTimelinePage/Timeline/TableBodyOperationsTimelineNL"
import { useLocation, useMatch, useNavigate } from "@tanstack/react-router"
import { ColumnDef, Row } from "@tanstack/react-table"
import { IconButton, LoadingCircle, PageCard, TableGeneral, Tooltip } from "@ui-lib/src"
import { common } from "@utils/common"
import { PortalFeature, TimeseriesCollection } from "api"
import { nb } from "date-fns/locale"
import { useEffect, useMemo, useState } from "react"
import DatePicker from "react-datepicker"
import { useTranslation } from "react-i18next"
import {
	MdCheckBox,
	MdCheckBoxOutlineBlank,
	MdClose,
	MdInfoOutline,
	MdKeyboardArrowRight,
	MdOutlineAssessment,
	MdOutlineTrendingDown,
	MdOutlineTrendingUp,
} from "react-icons/md"
import { PiTrendDownFill, PiTrendUpFill } from "react-icons/pi"
import type { TimelineViewMode } from "./aquacultureTimeline"
import {
	AquacultureTimelineViewSwitch,
	buildAquacultureTimelineRows,
	useAquacultureTimelineColumns,
	useAquacultureTimelineData,
} from "./aquacultureTimeline"
import {
	buildFuelTimelineRowsFromEndpointEvents,
	type FuelConsumptionTimelineTableItem,
} from "./operationTimelineRows"
import { useFuelEventsMetaDataQuery } from "./queries/useFuelEventsMetaDataQuery"
import { useListOfWeatherSensorsQuery } from "./queries/useListOfWeatherSensorsQuery"

export type { EventTableEntry, FuelConsumptionTimelineTableItem } from "./operationTimelineRows"

const sortDate = (t1: string | Date, t2: string | Date): number => {
	const timeDiff =
		(typeof t1 === "string" ? new Date(t1).getTime() : t1.getTime()) -
		(typeof t2 === "string" ? new Date(t2).getTime() : t2.getTime())

	if (timeDiff > 0) return 1
	if (timeDiff < 0) return -1

	return 0
}

type ArrayProp = {
	[key: string]: number[]
}

export type FuelConsumptionTimeline = {
	id: number
	date: string
	mode: string
	speed: number[]
	duration: number
	baseline: number
	consumers: {
		[key: string]: { consumption: number[]; load: number[] }
	}
	weather: ArrayProp
}

type InferAccessorType<T extends keyof FuelConsumptionTimelineTableItem> =
	FuelConsumptionTimelineTableItem[T]

export function formatDate(date: Date): string {
	return `${date.getDate() < 10 ? `0${date.getDate()}` : date.getDate()}/${
		date.getMonth() + 1 < 10 ? `0${date.getMonth() + 1}` : date.getMonth() + 1
	}/${date.getFullYear()}, ${date.getHours() < 10 ? `0${date.getHours()}` : date.getHours()}:${
		date.getMinutes() < 10 ? `0${date.getMinutes()}` : date.getMinutes()
	}:${date.getSeconds() < 10 ? `0${date.getSeconds()}` : date.getSeconds()}`
}

const calcFuelConsumptionFromFuelRateData = (fuelConsumersData: TimeseriesCollection) => {
	const millisecondsInHours = 1000 * 60 * 60
	let acc = 0
	for (const consumer of fuelConsumersData) {
		for (let i = 0; i < consumer.dataPoints.length - 1; i++) {
			const consumerDataPointsI = consumer.dataPoints[i]
			const consumerDataPointsIplus1 = consumer.dataPoints[i + 1]
			if (consumerDataPointsI === undefined || consumerDataPointsIplus1 === undefined) {
				continue
			}
			const timeDif =
				new Date(consumerDataPointsI.timestamp).getTime() -
				new Date(consumerDataPointsIplus1.timestamp).getTime()
			const area =
				((consumerDataPointsIplus1.value + consumerDataPointsI.value) * timeDif) /
				(2 * millisecondsInHours)
			acc = acc + area
		}
	}
	return acc
}

export const COMBINED_MODES = [
	{
		name: "Port - All",
		id: "port-all",
		refString: "port",
		splitString: "Port - ",
		combinedModeName: "Port",
	},
	{
		name: "Transit - All",
		id: "transit-all",
		refString: "transit",
		splitString: "Transit ",
		combinedModeName: "Transit",
	},
	{
		name: "DP - All",
		id: "dp-all",
		refString: "dp",
		splitString: "DP ",
		combinedModeName: "DP",
	},
]

function useFuelConsumptionTimelineTableColumns(
	showAquacultureColumns: boolean,
	viewMode: TimelineViewMode,
	hasOpModeData: boolean,
	baselineUnit?: string
) {
	const { t, i18n } = useTranslation()
	const relationshipColumns = useAquacultureTimelineColumns({
		isEnabled: showAquacultureColumns,
		viewMode,
		hasOpModeData,
	})

	const columns = useMemo<ColumnDef<FuelConsumptionTimelineTableItem>[]>(
		() => [
			{
				id: "expander",
				header: t("DETAILS"),
				cell: (info) => (
					<span
						id="expander-cell"
						className={`text-dipai-text-secondary hover:text-dipai-text-highlight flex items-center justify-center hover:cursor-pointer`}
						onClick={() => info.row.toggleExpanded()}
					>
						<MdKeyboardArrowRight
							className={`size-7 ${info.row.getIsExpanded() === true ? "rotate-90" : "rotate-0"} transition-[rotate] duration-500 ease-in-out hover:scale-110`}
						/>
					</span>
				),
			},
			{
				accessorKey: "id",
				header: t("ID"),
				enableHiding: true,
			},
			{
				accessorKey: "eventData",
				header: t("EVENT DATA"),
				enableHiding: true,
			},
			{
				accessorKey: "date",
				header: t("START"),
				cell: (info) => (
					<div className="flex items-center capitalize">
						{CellSetterV2({
							content: {
								type: "text",
								text: formatString(
									formatDate(
										new Date(info.getValue() as InferAccessorType<"date">)
									),
									i18n.language
								),
							},
							index: info.cell.id,
						})}
					</div>
				),
				sortingFn: (
					rowA: Row<FuelConsumptionTimelineTableItem>,
					rowB: Row<FuelConsumptionTimelineTableItem>,
					id: string
				) => {
					const aContent = rowA.getValue(id) as string | Date
					const bContent = rowB.getValue(id) as string | Date
					return sortDate(aContent, bContent)
				},
			},
			{
				accessorKey: "dateEnd",
				header: t("END"),
				cell: (info) => (
					<div className="flex items-center capitalize">
						{CellSetterV2({
							content: {
								type: "text",
								text: formatString(
									formatDate(
										new Date(info.getValue() as InferAccessorType<"dateEnd">)
									),
									i18n.language
								),
							},
							index: info.cell.id,
						})}
					</div>
				),
				sortingFn: (
					rowA: Row<FuelConsumptionTimelineTableItem>,
					rowB: Row<FuelConsumptionTimelineTableItem>,
					id: string
				) => {
					const aContent = rowA.getValue(id) as string | Date
					const bContent = rowB.getValue(id) as string | Date
					return sortDate(aContent, bContent)
				},
			},
			...relationshipColumns,
			{
				accessorKey: "duration",
				header: () => <div className={`flex w-full justify-end`}>{t("DURATION")}</div>,
				cell: (info) => {
					return CellSetterV2({
						content: {
							type: "number-with-unit",
							value: info.getValue() as InferAccessorType<"duration">,
						},
						index: info.cell.id,
					})
				},
				sortingFn: (
					rowA: Row<FuelConsumptionTimelineTableItem>,
					rowB: Row<FuelConsumptionTimelineTableItem>,
					id: string
				) => {
					const aContent = rowA.getValue(id)
					const bContent = rowB.getValue(id)
					if (typeof aContent === "string" && typeof bContent === "string") {
						const aSplit = aContent.split("h")
						const bSplit = bContent.split("h")
						const aMinutes =
							(isNaN(Number(aSplit[1])) !== true ? Number(aSplit[1]) : 0) +
							60 * (isNaN(Number(aSplit[0])) !== true ? Number(aSplit[0]) : 0)
						const bMinutes =
							(isNaN(Number(bSplit[1])) !== true ? Number(bSplit[1]) : 0) +
							60 * (isNaN(Number(bSplit[0])) !== true ? Number(bSplit[0]) : 0)

						if (aMinutes > bMinutes) return 1
						if (aMinutes < bMinutes) return -1
					}
					return 0
				},
			},
			{
				accessorKey: "totalFuel",
				header: () => (
					<div
						className={`flex w-full justify-end text-end`}
					>{`${t("TOTAL FUEL")} (m³)`}</div>
				),
				cell: (info) =>
					CellSetterV2({
						content: {
							type: "number-with-unit",
							value: info.getValue() as InferAccessorType<"totalFuel">,
						},
						index: info.cell.id,
					}),
			},
			{
				accessorKey: "deviation",
				header: () => (
					<div className={`flex w-full justify-end text-end`}>
						{baselineUnit !== undefined && baselineUnit.toLowerCase() !== "l/h"
							? t("BASELINE DEVIATION")
							: t("FUEL BASELINE DEVIATION")}
					</div>
				),
				cell: (info) =>
					CellSetterV2({
						content: {
							type: "fuel-deviation-from-baseline",
							baseline: (info.getValue() as InferAccessorType<"deviation">).percent,
							fuelDeviation: (info.getValue() as InferAccessorType<"deviation">).fuel,
							baselineUnit: baselineUnit,
						},
						index: info.cell.id,
					}),
				sortingFn: (
					rowA: Row<FuelConsumptionTimelineTableItem>,
					rowB: Row<FuelConsumptionTimelineTableItem>,
					id: string
				) => {
					const aContent = (rowA.getValue(id) as InferAccessorType<"deviation">).percent
					const bContent = (rowB.getValue(id) as InferAccessorType<"deviation">).percent
					if (typeof aContent === "number" && typeof bContent === "number") {
						if (aContent > bContent) return 1
						if (aContent < bContent) return -1
					}
					return 0
				},
			},
		],
		[i18n.language, relationshipColumns, t, baselineUnit]
	)
	return columns
}

type FuelConsumptionDetailContentProps = {}

export const MGO_DENSITY = 0.84 //tonne/L

export default function OperationsTimelineContent(props: FuelConsumptionDetailContentProps) {
	const { t, i18n } = useTranslation()
	const userData = useAppSelector(getUserData)
	const devFeatures = useGetDevFeatures()
	const [timelineViewMode, setTimelineViewMode] = useState<TimelineViewMode>("opmode")
	const [shouldSeparateLongEvents, setShouldSeparateLongEvents] = useState<boolean>(true)

	// created by AI
	useEffect(() => {
		if (userData.portalMode !== PortalMode.DEV && shouldSeparateLongEvents === false) {
			setShouldSeparateLongEvents(true)
		}
	}, [shouldSeparateLongEvents, userData.portalMode])

	const currentLocation = useLocation().pathname

	const search = useMatch({
		from: currentLocation.includes("clients")
			? "/clients/$clientName/_sopPageLayout/operations"
			: "/_sopPageLayout/operations",
		shouldThrow: false,
	})?.search

	const navigate = useNavigate({
		from: currentLocation.includes("clients")
			? "/clients/$clientName/operations"
			: "/operations",
	})

	const paramSelectedVessel = search?.vessel ?? ""

	const currentCompany = useAppSelector(getCurrentCompany)
	const plantData = common.plantDataFromOwnerAndName.get(
		`${currentCompany?.toLowerCase()}/${paramSelectedVessel?.toLowerCase()}`
	)

	const {
		isLoading: isOpModesListLoading,
		isError: isOpModesListError,
		data: opModesList,
	} = useGetOpModesListQuery({ plantIdsList: [plantData?.id ?? -1] })
	const hasOpModeData = (opModesList?.length ?? 0) > 0

	const defaultOpMode = "all"
	const paramOpMode = search?.opMode ?? defaultOpMode
	const [opMode, setOpMode] = useState<number[]>()

	const defaultMonth = LABELS_MONTHS?.[new Date().getMonth()]?.[1]?.toLowerCase() ?? "january"
	const paramMonth = search?.month ?? defaultMonth

	const defaultYear = new Date().getFullYear()
	const paramYear = search?.year?.toString() ?? defaultYear.toString()

	const now = new Date()
	const [range, setRange] = useState<string>(defaultMonth)
	const [year, setYear] = useState<number>(defaultYear)
	const [selectedDate, setSelectedDate] = useState<Date>(
		new Date(now.getFullYear(), now.getMonth())
	)

	const newDate = new Date(selectedDate)
	const newSelectedDate = new Date(selectedDate)
	newSelectedDate.getMonth()
	newDate.setMonth(newDate.getMonth() + 1)

	const gteDate = newSelectedDate
	const ltDate = newDate

	const {
		isLoading: isFuelEventsMetaDataLoading,
		isError: isFuelEventsMetaDataError,
		data: fuelEventsMetaData,
	} = useFuelEventsMetaDataQuery({
		opModesId: opMode ?? [-1],
		plantId: plantData?.id ?? -1,
		gte: gteDate,
		lt: ltDate,
	})

	const {
		isLoading: isFuelEventsMetaDataLoading2,
		isError: isFuelEventsMetaDataError2,
		data: fuelBaselineAvgData,
	} = useFuelEventsMetaDataQuery({
		opModesId: opModesList?.map((a) => a.id) ?? [-1],
		plantId: plantData?.id ?? -1,
		gte: gteDate,
		lt: ltDate,
	})

	const [averageBaselines, setAverageBaselines] = useState<CardData[] | undefined>([])

	useEffect(() => {
		if (fuelBaselineAvgData !== undefined && fuelBaselineAvgData !== null) {
			const res: CardData[] = []
			for (const index of fuelBaselineAvgData) {
				if (index.events.map((a) => a.fuelBaselineM3).length == 0) {
					continue
				}
				const tempBaseline = index.events
					.map((b) => (b.fuelBaselineM3 !== null ? b.fuelBaselineM3 : 0))
					.reduce((a, b) => a + b, 0)

				if (tempBaseline === 0) {
					continue
				}

				const tempCubics = index.events
					.map((b) => b.fuelActualM3)
					.reduce((a, b) => a + b, 0)

				res.push({
					opMode:
						opModesList?.find((mode) => mode.id === index.metaData.opModeId)?.name ??
						"",
					deviation: (100 * tempCubics) / tempBaseline - 100,
					deviationFuel: tempCubics - tempBaseline,
					baselineName: index.metaData.baselineConfigName ?? "",
					baselineUnit: index.metaData.baselineUnit ?? undefined,
				})
			}
			res.sort((a, b) => a.opMode?.localeCompare(b.opMode))

			setAverageBaselines(res)
		}
	}, [fuelBaselineAvgData])

	const {
		isLoading: isListOfWeatherSensorsLoading,
		isError: isListOfWeatherSensorsError,
		data: listOfWeatherSensors,
	} = useListOfWeatherSensorsQuery({
		plantId: plantData?.id ?? -1,
	})

	useEffect(() => {
		if (opModesList === undefined || opModesList === null) {
			return
		}
		if (opModesList?.length === 0) {
			return
		}
		const resolvedDefaultOpMode = [
			...opModesList?.map((o) => o.name.toLowerCase()),
			"all",
			...COMBINED_MODES.map((cm) => cm.id.toLowerCase()),
		].includes(paramOpMode.toLowerCase())
			? paramOpMode.toLowerCase()
			: defaultOpMode

		const element = document.getElementById("opModeSelect") as HTMLInputElement
		if (element !== null) {
			element.value = (() => {
				if (resolvedDefaultOpMode === "all") {
					return "all"
				}
				for (const mode of COMBINED_MODES) {
					if (resolvedDefaultOpMode === mode.id) {
						return mode.id
					}
				}
				return `${
					opModesList.find((o) => o.name.toLowerCase() === resolvedDefaultOpMode)?.id!
				}`
			})()
		}

		handleOpModeUpdate(resolvedDefaultOpMode, opModesList)
	}, [opModesList])
	const test = currentCompany === undefined ? false : true

	const canUserSeeBaseline2 =
		currentCompany === undefined
			? false
			: isFeatureAvailable(
					undefined,
					PortalFeature.CONSUMPTION_TIMELINE_BASELINE,
					currentCompany,
					devFeatures,
					userData.portalMode
				)

	const canUserSeeBaseline =
		currentCompany === undefined
			? true // false
			: test

	const aquacultureTimeline = useAquacultureTimelineData({
		plantId: plantData?.id,
		currentCompany,
		devFeatures,
		portalMode: userData.portalMode,
		gte: gteDate,
		lt: ltDate,
	})

	// Fish-only vessels (no op-modes) would land on the empty op-mode view, so default them
	// to the fish-log view once we know the vessel has no op-modes.
	useEffect(() => {
		if (
			aquacultureTimeline.isEnabled === true &&
			isOpModesListLoading === false &&
			hasOpModeData === false
		) {
			setTimelineViewMode("fishmode")
		}
	}, [aquacultureTimeline.isEnabled, isOpModesListLoading, hasOpModeData])

	const data: FuelConsumptionTimelineTableItem[] = useMemo(() => {
		if (currentCompany === undefined) {
			return []
		}
		// Fish-only vessels (UniSea operations module) have no fuel/op-mode events, but we
		// still build the timeline so the aquaculture path can render fish log events.
		const hasFuelData = fuelEventsMetaData !== undefined && fuelEventsMetaData.length > 0
		if (hasFuelData === false && aquacultureTimeline.isEnabled !== true) {
			return []
		}

		const { rawOpModeEvents, rows: combinedEvents } = buildFuelTimelineRowsFromEndpointEvents({
			canUserSeeBaseline,
			fuelEventsMetaData,
			isFilteringSpecificOpMode: opMode?.length === 1,
			listOfWeatherSensors,
			opModesList,
			rangeEnd: ltDate,
			rangeStart: gteDate,
			shouldSeparateLongEvents,
			unrecognizedModeLabel: t("UNRECOGNIZED MODE"),
		})
		if (aquacultureTimeline.isEnabled === false) {
			return combinedEvents
		}
		return buildAquacultureTimelineRows({
			baseRows: combinedEvents,
			rawOpModeEvents,
			fishLogEvents: aquacultureTimeline.fishLogEvents,
			viewMode: timelineViewMode,
			rangeStart: gteDate,
			rangeEnd: ltDate,
			canUserSeeBaseline,
		})
	}, [
		range,
		year,
		opMode,
		fuelEventsMetaData?.length,
		currentCompany,
		aquacultureTimeline,
		timelineViewMode,
		gteDate,
		ltDate,
		canUserSeeBaseline,
		opModesList,
		listOfWeatherSensors,
		shouldSeparateLongEvents,
		i18n.language,
	])

	const columns = useFuelConsumptionTimelineTableColumns(
		aquacultureTimeline.isEnabled,
		timelineViewMode,
		hasOpModeData,
		fuelEventsMetaData?.[0]?.metaData.baselineUnit ?? undefined
	)
	const expandedResetKey = `${paramMonth}|${paramYear}|${paramOpMode}|${timelineViewMode}|${shouldSeparateLongEvents}`
	const handleOpModeUpdate = (
		opModeRef: string,
		opModesList: {
			id: number
			name: string
		}[]
	) => {
		if (opModeRef === "all") {
			setOpMode(opModesList.map((opMode) => opMode.id))
			return
		}
		for (const mode of COMBINED_MODES) {
			if (opModeRef === mode.id) {
				setOpMode(
					opModesList
						.filter((opMode) => opMode.name.toLowerCase().includes(mode.refString))
						.map((opMode) => opMode.id)
				)
				return
			}
		}
		setOpMode([
			opModesList.find((o) => o.name.toLowerCase() === opModeRef.toLowerCase())?.id ?? -1,
		])
		return
	}

	useEffect(() => {
		if (opModesList === undefined || opModesList === null) {
			return
		}
		handleOpModeUpdate(
			(() => {
				if (paramOpMode === "all") {
					return "all"
				}
				for (const mode of COMBINED_MODES) {
					if (paramOpMode === mode.id.toLowerCase()) {
						return mode.id.toLowerCase()
					}
				}
				const opModeTemp = opModesList.find(
					(o) => o.name.toLowerCase() === paramOpMode.toLowerCase()
				)
				return opModeTemp !== undefined ? opModeTemp.name.toLowerCase() : "all"
			})(),
			opModesList
		)
	}, [paramOpMode])

	useEffect(() => {
		const monthIndex = LABELS_MONTHS.find((month) => month[1].toLowerCase() === paramMonth)?.[2]
		if (
			monthIndex !== undefined &&
			(selectedDate.getFullYear().toString() !== paramYear ||
				selectedDate.getMonth() !== monthIndex)
		) {
			setSelectedDate(
				new Date(
					isNaN(Number(paramYear)) === true
						? new Date().getFullYear()
						: Number(paramYear),
					monthIndex
				)
			)
		}
		setRange(paramMonth)
		setYear(isNaN(Number(paramYear)) === true ? new Date().getFullYear() : Number(paramYear))
	}, [paramMonth, paramYear])

	const [columnLayout, setColumnsLayout] = useState<string>(
		"grid-cols-[50px_minmax(100px,1fr)__minmax(100px,1fr)__minmax(100px,1fr)__minmax(100px,1fr)_minmax(100px,1fr)_minmax(100px,1fr)]"
	)

	useEffect(() => {
		if (canUserSeeBaseline === false && columns.length > 8) {
			columns.pop()
			setColumnsLayout(
				"grid-cols-[50px_minmax(100px,1fr)__minmax(100px,1fr)__minmax(100px,1fr)__minmax(50px,1fr)__minmax(100px,1fr)]"
			)
		} else if (aquacultureTimeline.isEnabled === true) {
			// Fish-only vessels drop the two op-mode columns, so use a narrower grid so the
			// remaining columns stretch to fill the width.
			setColumnsLayout(
				timelineViewMode === "fishmode" && hasOpModeData === false
					? "grid-cols-[50px_repeat(6,minmax(100px,1fr))]"
					: "grid-cols-[50px_repeat(8,minmax(100px,1fr))]"
			)
		} else {
			setColumnsLayout(
				"grid-cols-[50px_minmax(100px,1fr)__minmax(100px,1fr)__minmax(100px,1fr)__minmax(100px,1fr)_minmax(100px,1fr)_minmax(100px,1fr)]"
			)
		}
	}, [
		aquacultureTimeline.isEnabled,
		canUserSeeBaseline,
		columns,
		timelineViewMode,
		hasOpModeData,
	])

	return (
		<>
			<div
				className={`shadow-card sticky top-13 z-10 -mt-5 mb-5 -ml-5 flex h-12.25 w-[calc(100%+40px)] flex-row items-center justify-end gap-4 border-b-1 bg-white px-5`}
			>
				{opModesList !== undefined ? (
					<div className={`flex items-center gap-3`}>
						<div className={`relative`}>
							<MdOutlineAssessment
								className={`text-dipai-secondary-800 absolute top-2 ml-2`}
							/>
							<select
								className={`border-dipai-text-muted text-small hover:border-dipai-text-highlight focus:border-dipai-text-highlight mx-px h-8 w-auto rounded-sm border-1 bg-transparent py-0.5 pl-8 hover:cursor-pointer focus:mx-0 focus:border-2 focus:ring-0 focus:outline-hidden`}
								value={(() => {
									if (
										opMode === undefined ||
										opModesList === null ||
										opMode.length === opModesList.length
									) {
										return "all"
									}
									if (opMode.length === 1) {
										return opModesList.find((o) => o.id === opMode[0])?.id
									}
									for (const mode of COMBINED_MODES) {
										const refName =
											opModesList
												.find((o) => o.id === opMode[0])
												?.name.split(" ")[0]
												?.toLowerCase() ?? "unknown mode"
										if (mode.name.toLowerCase().includes(refName) === true) {
											return mode.id
										}
									}
								})()}
								id="opModeSelect"
								name="opMode"
								onChange={(e) => {
									navigate({
										to: ".",
										search: (prev) => ({
											...prev,
											opMode: (() => {
												if (
													e.target.value === "all" ||
													opModesList === null
												) {
													return "all"
												}
												for (const mode of COMBINED_MODES) {
													if (e.target.value === mode.id) {
														return mode.id
													}
												}
												return opModesList.find(
													(o) => o.id === Number(e.target.value)
												)?.name!
											})().toLowerCase(),
										}),
									})
								}}
							>
								{[
									...(opModesList !== null ? opModesList : []),
									{ name: `All modes`, id: "all" },
									...COMBINED_MODES.filter((mode) => {
										if (opModesList !== undefined && opModesList !== null) {
											return opModesList?.filter((a) =>
												a.name.toLowerCase().includes(mode.refString)
											).length > 1
												? true
												: false
										}
										return false
									}),
								]
									.sort((a, b) => {
										if (a.name === `All modes`) return -1
										if (b.name === `All modes`) return 1
										return a.name.localeCompare(b.name)
									})
									.map((opMode) => (
										<option
											key={`opModeOption_${opMode.name}_${opMode.id}`}
											value={opMode.id}
										>
											{opMode.name}
										</option>
									))}
							</select>
						</div>
						<AquacultureTimelineViewSwitch
							viewMode={timelineViewMode}
							setViewMode={setTimelineViewMode}
						/>
						{userData.portalMode === PortalMode.DEV ? (
							<Tooltip
								className={"bg-dipai-secondary-800/95"}
								arrowClassName={"fill-dipai-secondary-800/95"}
								align={"center"}
								customContent={t("SEPARATE 48H+ EVENTS")}
							>
								<button
									aria-pressed={shouldSeparateLongEvents}
									className={`border-dipai-text-muted text-small hover:border-dipai-text-highlight focus:border-dipai-text-highlight flex h-8 items-center gap-2 rounded-sm border-1 bg-transparent px-3 hover:cursor-pointer focus:border-2 focus:outline-hidden ${
										shouldSeparateLongEvents === true
											? "text-dipai-text-highlight"
											: "text-dipai-text-primary"
									}`}
									type="button"
									onClick={() => setShouldSeparateLongEvents((prev) => !prev)}
								>
									{shouldSeparateLongEvents === true ? (
										<MdCheckBox className="h-4 w-4" />
									) : (
										<MdCheckBoxOutlineBlank className="h-4 w-4" />
									)}
									<span>{t("SEPARATE 48H+ EVENTS")}</span>
								</button>
							</Tooltip>
						) : null}
					</div>
				) : null}
				<div className={`border-dipai-text-muted flex flex-row border-l-1 pl-4`}>
					<DatePicker
						id={"time-selection"}
						calendarContainer={CustomCalendarContainer}
						customInput={<CustomDatePickerInput width={112} />}
						locale={i18n.language === "no" ? nb : undefined}
						showMonthYearPicker
						dateFormat="MMM yyyy"
						maxDate={new Date()}
						minDate={
							plantData?.id !== undefined &&
							[1089, 1091, 1092, 1090].includes(plantData?.id)
								? new Date(2022, 0)
								: new Date(2023, 0)
						}
						onChange={(date) => {
							if (date !== null) {
								const selectedMonthLabel = LABELS_MONTHS.find(
									(month) => month[2] === date.getMonth()
								)?.[1]
								if (selectedMonthLabel === undefined) {
									return
								}
								navigate({
									to: ".",
									search: (prev) => ({
										...prev,
										month: selectedMonthLabel ?? defaultMonth,
										year: date.getFullYear(),
									}),
								})
								setSelectedDate(date)
							}
						}}
						popperPlacement="top-end"
						selected={selectedDate}
						selectsStart
						startDate={selectedDate}
					/>
				</div>
				{/* </div> */}
			</div>
			{isFuelEventsMetaDataLoading === false && isOpModesListLoading === false ? (
				data !== undefined && data.length > 0 ? (
					<div className={`flex flex-col gap-5`}>
						{/* AHTS WARNING PANEL */}
						<AHTSWaningPanel plantName={plantData?.display_name} />

						{canUserSeeBaseline === false ? null : (
							<DeviationCards cardData={averageBaselines} />
						)}
						<div
							id="table-container"
							className={`shadow-card h-fit w-full rounded-sm bg-white p-3 px-4`}
						>
							<div className={`-mx-4 -mt-3`}>
								<TableGeneral
									translationFunc={t}
									columnVisibility={{ id: false, eventData: false }}
									TableBody={TableBodyOperationsTimelineNL}
									data={data}
									columns={columns}
									columnsLayout={columnLayout}
									expandedResetKey={expandedResetKey}
									itemsPerPage={10}
									loading={false}
									showGlobalFilter={true}
								/>
							</div>
						</div>
					</div>
				) : (
					<div
						className={`flex h-[calc(100vh-52px-49px-40px)] w-full flex-col items-center pt-[calc((100vh-52px-460px-49px-40px)/3)]`}
					>
						<PlaceHolderShipImgContainer companyName={currentCompany} />
						<p className="text-title4 pb-2 font-semibold opacity-75">
							{t(
								"NO FUEL CONSUMPTION DATA AVAILABLE FOR THIS COMBINATION OF TIME PERIOD AND OPERATION MODE"
							)}
						</p>
						<p className="text-headline opacity-50">
							{t("PLEASE SELECT A DIFFERENT TIME PERIOD OR/AND OPERATION MODE")}
						</p>
					</div>
				)
			) : (
				<div
					className={`flex h-[calc(100vh-20px-20px-49px-52px)] flex-row items-center justify-center`}
				>
					<LoadingCircle containerHeightWithUnit="50%-20px" />
				</div>
			)}
		</>
	)
}

interface CardData {
	opMode: string
	deviation: number
	deviationFuel: number
	baselineName?: string
	baselineUnit?: string
}

function DeviationCards({ cardData }: { cardData: CardData[] | undefined }) {
	const { t } = useTranslation()
	const [isModalOpen, setModalOpen] = useState<boolean>(false)
	const fuelBaselineDeviationCard = document.getElementById("fuel-baseline-deviation-card")
	const isFuelBaseline =
		(cardData?.[0]?.baselineUnit !== undefined &&
			cardData[0].baselineUnit.toLowerCase() !== "l/h") === false
	return (
		<PageCard
			id={"fuel-baseline-deviation-card"}
			className={`min-h-0`}
		>
			<PageCard.Section
				variant={"header"}
				label={
					isFuelBaseline
						? t("AVERAGE FUEL BASELINE DEVIATION PER MODE")
						: t("AVERAGE BASELINE DEVIATION PER MODE")
				}
			>
				<div className={`-ml-4 flex h-full items-center`}>
					<MdInfoOutline
						className={`h-5 w-5 hover:cursor-pointer`}
						onClick={() => setModalOpen(true)}
					/>
				</div>
			</PageCard.Section>
			<ModalNL
				isOpen={isModalOpen}
				setIsOpen={setModalOpen}
			>
				<div
					className={`fixed top-40 z-5 flex h-50 w-200 flex-col gap-2 rounded-sm bg-white p-4`}
					style={{
						left: `${
							fuelBaselineDeviationCard !== null
								? ` ${
										-400 +
										fuelBaselineDeviationCard?.offsetLeft +
										fuelBaselineDeviationCard?.offsetWidth / 2
									}px`
								: ``
						}`,
					}}
				>
					<div className={`flew-row mb-2 flex items-center justify-between font-bold`}>
						<p className={`text-body`}>
							{isFuelBaseline
								? t(`${"FUEL BASELINE PREDICTION MODEL"}`)
								: t(`${"BASELINE PREDICTION MODEL"}`)}
						</p>
						<IconButton
							variant={"text-dark"}
							size={"sm"}
							onClick={() => setModalOpen((prev) => !prev)}
						>
							<IconButton.Icon>{MdClose}</IconButton.Icon>
						</IconButton>
					</div>
					<div className={`mb-5 flex h-auto w-full flex-col gap-1 p-2`}>
						<p>{`${t(
							"THE BASELINE DATA IS ALL THE VESSEL DATA LOGGED BEFORE THE BEGINNING OF THE CURRENT YEAR"
						)}`}</p>
						<div>
							<div className="grid grid-cols-3">
								<div className="col-span-1 flex flex-col items-center justify-center">
									<div className="flex flex-row items-center justify-center">
										<div className={`m-2 text-green-600`}>{`-2.5%`}</div>
										<div
											className={`flex h-7 w-7 items-center justify-center rounded-full border-2 border-green-600 text-green-600`}
										>
											<PiTrendDownFill className="h-5 w-5" />
										</div>
									</div>
									<div
										className={`text-gray-666 max-h-5 overflow-hidden pb-2 text-center font-bold text-clip`}
									>
										{t(`BELOW BASELINE`)}
									</div>
								</div>
								<div className="col-span-1 flex flex-col items-center justify-center">
									<div className="flex flex-row items-center justify-center">
										<div className={`m-2 text-amber-600`}>{`2.5%`}</div>
										<div
											className={`flex h-7 w-7 items-center justify-center rounded-full border-2 border-amber-600 text-amber-600`}
										>
											<PiTrendUpFill className="h-5 w-5" />
										</div>
									</div>
									<div
										className={`text-gray-666 max-h-5 overflow-hidden pb-2 text-center font-bold text-clip`}
									>
										{t("ABOVE BASELINE")}
									</div>
								</div>
								<div className="col-span-1 flex flex-col items-center justify-center">
									<div className="flex flex-row items-center justify-center">
										<div className={`m-2 text-red-600`}>{`5.2%`}</div>
										<div
											className={`flex h-7 w-7 items-center justify-center rounded-full border-2 border-red-600 text-red-600`}
										>
											<PiTrendUpFill className="h-5 w-5" />
										</div>
									</div>
									<div
										className={`text-gray-666 max-h-5 overflow-hidden pb-2 text-center font-bold text-clip`}
									>
										{t("ABOVE BASELINE > 5 %")}
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</ModalNL>
			{cardData === undefined || cardData.length === 0 ? (
				<>
					<div className="grid max-h-16 w-auto grid-cols-1 items-center border-1 p-2">
						<p className={`text-small text-gray-666 max-h-5 text-center`}>
							{t("COLLECTING HISTORICAL SENSOR DATA...")}
						</p>
					</div>
				</>
			) : (
				<>
					<div
						className="grid h-auto w-auto gap-3 text-sm"
						style={{
							display: "grid",
							gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
							justifyContent: "center",
						}}
					>
						{cardData.map((value, index) => (
							<div
								key={index}
								className="border-dipai-text-disabled rounded-sm border-1 p-2"
							>
								<div className="overflow-hidden">
									<Tooltip
										className={"bg-dipai-secondary-800/95"}
										arrowClassName={"fill-dipai-secondary-800/95"}
										align={"center"}
										customContent={`${value.deviationFuel.toFixed(3)} ${
											value.baselineUnit !== undefined &&
											value.baselineUnit.toLowerCase() !== "l/h"
												? "tonnesCO₂"
												: "m³"
										}`}
									>
										<div className="flex flex-row items-center justify-center">
											<div
												className={`m-2 ${
													value.deviation === -Infinity ||
													value.deviation === Infinity ||
													value.deviation === null ||
													value.deviation === 0
														? `text-slate-700`
														: value.deviation > 0
															? value.deviation >= 5
																? `text-red-600`
																: `text-amber-600`
															: `text-green-600`
												}`}
											>
												{value.deviation === -Infinity ||
												value.deviation === Infinity ||
												value.deviation === null ||
												value.deviation === 0
													? `--%`
													: `${value.deviation.toFixed(2)}%`}
											</div>

											<div
												className={`flex h-7 w-7 items-center justify-center rounded-full border-2 ${
													value.deviation === -Infinity ||
													value.deviation === Infinity ||
													value.deviation === null ||
													value.deviation === 0
														? `border-slate-700 text-slate-700`
														: value.deviation > 0
															? value.deviation >= 5
																? `border-red-600 text-red-600`
																: `border-amber-600 text-amber-600`
															: `border-green-600 text-green-600`
												}`}
											>
												{value.deviation === -Infinity ||
												value.deviation === Infinity ||
												value.deviation === null ||
												value.deviation === 0 ? (
													"---"
												) : value.deviation > 0 ? (
													<MdOutlineTrendingUp className="h-5 w-5" />
												) : (
													<MdOutlineTrendingDown className="h-5 w-5" />
												)}
											</div>
										</div>
									</Tooltip>
								</div>
								<Tooltip
									className={"bg-dipai-secondary-800/95"}
									arrowClassName={"fill-dipai-secondary-800/95"}
									align={"center"}
									customContent={cardData[index]?.opMode ?? "---"}
								>
									<p
										className={`text-dipai-text-primary max-h-6 overflow-hidden pb-2 text-center leading-6 font-semibold text-ellipsis whitespace-nowrap`}
									>
										{`${cardData[index]?.opMode ?? "---"}`}
									</p>
								</Tooltip>
								<p
									className={`text-dipai-text-secondary max-h-5 overflow-hidden pb-2 text-center font-normal text-ellipsis`}
								>
									{`${t("MODEL")}: ${cardData[index]?.baselineName !== "" ? cardData[index]?.baselineName : t("AUTO")}`.toLowerCase()}
								</p>
							</div>
						))}
					</div>
				</>
			)}
		</PageCard>
	)
}
